import * as React from 'react';
import CalendarSurfaceStore from '../../../store/schema/CalendarSurfaceStore';
import { minmax } from '../../minmax';
import { isPastDate } from 'owa-observable-datetime';
import { observer } from 'mobx-react-lite';
import { OwaDate, startOfDay, getDay } from 'owa-datetime';
import { createWorkWeekDaysArray } from 'owa-datetime-utils';
import WorkingHoursType from 'owa-service/lib/contract/WorkingHoursType';

const styles = require('./WorkingHoursContent.scss');

/**
 * Shows working hours shadings on grid column.
 *
 * Working hours have their own time zone, independent of the main time zone and
 * if these time zones are apart enough then working hours can bleed from one day
 * to the next. When this happens, we need to account not only if the current day
 * is a working day, but also if the previous or next day are working days.
 * (TODO, do we need to account for more than 1 day each way?)
 *
 * The server sends up workHours*TimeInMinutes values adjusted to the user's time zone.
 * So these values can be in one of three states:
 * - in the previous day, if workHours*TimeInMinutes < 0;
 * - in the current day, if 0 <= workHours*TimeInMinutes < 1440;
 * - in the next day, if workHours*TimeInMinutes >= 1440.
 *
 * We have 9 theoretical cases (3x3). However, the start value is always less than end,
 * so we have 5 possible cases to deal with.
 *
 * The following table describes what we need to render on a "this" day:
 * (tz is the user's time zone, whtz is the working hours time zone)
 *
 * |   start   |    end    | tz difference | visible working hours (all subject to the day in question being a work day) |
 * | :-------: | :-------: | :-----------: | --------------------------------------------------------------------------- |
 * |    s<0    |    e<0    |  tz <<< whtz  | [next_day.start, next_day.end] (this day is rendered by previous day)       |
 * |    s<0    | 0<=e<1440 |  tz << whtz   | [0, this_day.end] and [next_day.start, 1440]                                |
 * |    s<0    |  e>=1440  |      n/a      | difference between end and start is always < 1440 (1 day)                   |
 * | 0<=s<1440 |    e<0    |      n/a      | start is always < than end                                                  |
 * | 0<=s<1440 | 0<=e<1440 |  tz ~= whtz   | [this_day.start, this_day.end]                                              |
 * | 0<=s<1440 |  e>=1440  |  tz >> whtz   | [ 0, prev_day.end] and [this_day.start, 1440]                               |
 * |  s>=1440  |    e<0    |      n/a      | start is always < than end                                                  |
 * |  s>=1440  | 0<=e<1440 |      n/a      | start is always < than end                                                  |
 * |  s>=1440  |  e>=1440  |  tz >>> whtz  | [prev_day.start, prev_day.end]                                              |
 *
 * Once the working hours are determined, we need to flip it around and
 * produce the non-working hours shadows.
 */
export default observer(function WorkingHoursContent({
    surfaceStore,
    modelDate,
    calendarWorkingHours,
}: {
    surfaceStore: CalendarSurfaceStore;
    modelDate: OwaDate;
    calendarWorkingHours?: WorkingHoursType;
}) {
    const date = startOfDay(modelDate);

    // Previous days are completely shadowed
    const isPast = isPastDate(modelDate);

    // For other days, calculate the positions of start and end of working hours
    const { userOptions, dayStartMinute, dayLength } = surfaceStore;

    let { workHoursStartTimeInMinutes, workHoursEndTimeInMinutes, workDays } = userOptions;

    if (calendarWorkingHours) {
        workHoursStartTimeInMinutes = calendarWorkingHours.WorkHoursStartTimeInMinutes;
        workHoursEndTimeInMinutes = calendarWorkingHours.WorkHoursEndTimeInMinutes;
        workDays = createWorkWeekDaysArray(calendarWorkingHours.WorkDays);
    }

    // If the start offset is absolutely bigger than 1 day (1440 minutes) then we need
    // to offset all our math. This happens when time zones are very far apart.
    // For example, when work hours start at midnight in UTC+14 and user time zone is UTC-12:
    // In this situation, Monday 00:00 show up on the previous Saturday at 10pm.
    // With this offset (-1560), the Saturday column needs to calculate things as if it was Sunday
    // so that it looks at "next day" being Monday. The reverse happens if the offset is positive.
    const dayOffset =
        (workHoursStartTimeInMinutes < 0 ? 1 : -1) *
        Math.floor(Math.abs(workHoursStartTimeInMinutes) / 1440);

    const s = workHoursStartTimeInMinutes + dayOffset * 1440;
    const e = workHoursEndTimeInMinutes + dayOffset * 1440;
    const thisDay = (7 + getDay(date) + dayOffset) % 7;
    const prevDay = (7 + thisDay - 1) % 7;
    const nextDay = (7 + thisDay + 1) % 7;
    const thisIsWorkingDay = workDays.includes(thisDay);
    const prevIsWorkingDay = workDays.includes(prevDay);
    const nextIsWorkingDay = workDays.includes(nextDay);

    // Normalize the start and end minutes so they are positive numbers.
    // Worst case is making start at midnight in UTC+14 and using TimeZone UTC-12
    // which causes WorkHoursStartTimeInMinutes to be -1560. So we add 2880
    // to ensure the resulting value will be positive.
    const whStart = (2880 + s) % 1440;
    const whEnd = (2880 + e) % 1440;

    // |    s<0    |    e<0    | next_day.start
    // |    s<0    | 0<=e<1440 | next_day.start
    // | 0<=s<1440 | 0<=e<1440 | this_day.start
    // | 0<=s<1440 |  e>=1440  | this_day.start
    // |  s>=1440  |  e>=1440  | prev_day.start
    const whStartOrEndOfDay =
        (s < 0 && nextIsWorkingDay) ||
        (0 <= s && s < 1440 && thisIsWorkingDay) ||
        (s >= 1440 && prevIsWorkingDay)
            ? whStart
            : 1440;

    // |    s<0    |    e<0    | next_day.end
    // |    s<0    | 0<=e<1440 | this_day.end
    // | 0<=s<1440 | 0<=e<1440 | this_day.end
    // | 0<=s<1440 |  e>=1440  | prev_day.end
    // |  s>=1440  |  e>=1440  | prev_day.end
    const whEndOrStartOfDay =
        (e < 0 && nextIsWorkingDay) ||
        (0 <= e && e < 1440 && thisIsWorkingDay) ||
        (e >= 1440 && prevIsWorkingDay)
            ? whEnd
            : 0;

    // Clip the values to the visible range
    const clippedWhStart = minmax(0, whStartOrEndOfDay - dayStartMinute, dayLength);
    const clippedWhEnd = minmax(0, whEndOrStartOfDay - dayStartMinute, dayLength);

    // Generate the clipped blocks of shadows we want to draw
    const nonWorkingTimes: { start: number; end: number }[] = [];

    if ((whStartOrEndOfDay == 1440 && whEndOrStartOfDay == 0) || isPast) {
        // If the start and end are reversed and at the edges of the day then
        // this is not a working day and it is not showing working hours
        // from the previous or next day either, so we shadow the entire day.
        nonWorkingTimes.push({ start: 0, end: dayLength });
    } else if (whStart < whEnd) {
        // If the start and end times are in order then we have a block
        // of working hours in the middle of the day and we might have two
        // blocks of shadow, one before and one after the working hours,
        // unless they end up being clipped by the day start and day length settings.
        if (clippedWhStart) {
            nonWorkingTimes.push({ start: 0, end: clippedWhStart });
        }

        if (clippedWhEnd < dayLength) {
            nonWorkingTimes.push({ start: clippedWhEnd, end: dayLength });
        }
    } else if (whEnd < whStart) {
        // If the start and end times are reversed, then the time zones
        // are causing the working hours to happen across midnight so
        // we have a single block of shadow in the middle of the day.
        // The clipped values already account for the fact that previous
        // and next day might not be work days.
        nonWorkingTimes.push({ start: clippedWhEnd, end: clippedWhStart });
    }

    const elements = nonWorkingTimes.map(({ start, end }) => (
        <div
            key={`${start}_${end}`}
            style={{ top: start, height: end - start }}
            className={styles.nonWorkingTime}
        />
    ));

    return <>{elements}</>;
});
